import type { FC, ReactNode } from 'react';
import { Button } from '@mui/material';
import type { ButtonProps } from '@mui/material';
import { Link, LinkProps } from 'react-router-dom';

interface CTAButtonProps {
  to?: string;
  href?: string;
  children: ReactNode;
  variant?: 'text' | 'outlined' | 'contained';
  size?: 'small' | 'medium' | 'large';
  fullWidth?: boolean;
  disabled?: boolean;
  startIcon?: ReactNode;
  endIcon?: ReactNode;
  onClick?: () => void;
  sx?: ButtonProps['sx'];
}

const CTAButton: FC<CTAButtonProps> = ({
  to,
  href,
  children,
  variant = 'contained',
  size = 'large',
  sx,
  ...props
}) => {
  const baseStyles = {
    px: 4,
    py: 1.5,
    fontSize: '1.1rem',
    fontWeight: 600,
    borderRadius: 2,
    boxShadow: variant === 'contained' ? '0 4px 14px rgba(100, 181, 246, 0.4)' : 'none',
    '&:hover': {
      boxShadow: variant === 'contained' ? '0 6px 20px rgba(100, 181, 246, 0.5)' : 'none',
      transform: 'translateY(-2px)',
    },
    transition: 'all 0.2s ease-in-out',
    ...sx,
  };

  if (to) {
    return (
      <Button
        component={Link as React.ComponentType<LinkProps>}
        to={to}
        variant={variant}
        size={size}
        sx={baseStyles}
        {...props}
      >
        {children}
      </Button>
    );
  }

  if (href) {
    return (
      <Button
        href={href}
        variant={variant}
        size={size}
        sx={baseStyles}
        {...props}
      >
        {children}
      </Button>
    );
  }

  return (
    <Button variant={variant} size={size} sx={baseStyles} {...props}>
      {children}
    </Button>
  );
};

export default CTAButton;
